import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import { ReverseReasonDialog } from '../CompanyWorkspace/reverseReason';
import Pagination from '../Pagination';
import PrepaidSentence, { prepaidHoldOf, HandDoorLock } from './handPrepaid';

// ── Manual entries, accruals and reversals ──────────────────────────────────────────────────────
// Everything the derived drafts cannot produce. An entry typed here with no third-party document is
// an internal voucher (ใบสำคัญทั่วไป): the 2544 notification's ข้อ 4 wants the explanation and the
// calculation written down, so the form asks for them and the server refuses without them.
//
// Amounts are typed in baht and sent as integer satang — the ledger has no floats anywhere, and the
// conversion happens once, here, rather than in three places that drift.
//
// ⛔ Every door on this card writes — New (POST /journal/manual), Reverse and its Copy (POST
// …/reverse, …/copy), Import, whose Preview is itself a POST, and the accruals sweep — so a read-only
// seat (a client's own books login, the auditor — utils/readOnlySeat.ts) is shown none of them, the
// same as staff in a closed period. The server refuses each to that login and every refusal pages the
// admins. What it reads stays: the entry-deadline warnings. The posted journals are read elsewhere.

type Account = { id: string; code: string; name_th: string; name_en: string; class: string };
// `payroll` (batch 10 R7; R8 brief §4.3): what in payroll owns the entry — the run (its accrual), a payment of the
// run's net pay, or a PND.1 / SSO remittance — and the reversal of any of them. The route reads it through
// journalPost.payrollFamilyOf; null for every other entry. `runId` / `docNo` are '' for a remittance (it belongs to a
// month, not a run), and an R7 server sends no `kind` (its only kind was the run).
type PayrollRef = {
  kind?: 'run' | 'payment' | 'remittance'; runId: string; docNo: string;
  paymentId?: string; payNo?: string; remittanceId?: string; remitKind?: 'pnd1' | 'sso'; year?: number; month?: number;
};
// `reverseRefusal` (batch 11 R2): why the server would refuse to reverse this entry — its code and its English
// sentence (services/manualEntry.reverseRefusalOf), null when it would not. null on a payroll row too, which says who
// owns it instead.
// `byHand` (W1 ship): where the server's way out is a manual entry MADE FOR the source, in the open period, with
// nothing to do first (journalPost.releasedReversalVerdict) — the source, which Record by hand opens the form for.
// `closedPeriod` (W1 ship verification): a released reversal posted in a period CLOSED since, listed in the open period's
// picker only for its Record by hand (GET /journal/posted) — a closed period's Manual entries has no Reverse panel, so
// it had no door anywhere. Such a row says where it was posted and offers nothing else (its Copy would default to a
// date in the closed period, a request bound to be refused).
// `copyRefusal` (W1 ship): a manual entry made for a source by hand, or its reversal, is never copied — the copy door's
// 409 hand_copy (journalImport.copyRefusalOf). Such a row offers no Repeat; it says why, as a hover. Absent on every other row.
type HandSource = { kind: string; id: string; label: string };
type Posted = { id: string; entry_date: string; source_book: string; doc_no: string; narration: string; page_no: number; amount: number; reversed_by: string | null; auto_reverse_on: string;
  payroll?: PayrollRef | null; reverseRefusal?: { code: string; message: string; byHand?: HandSource } | null; closedPeriod?: { from: string; to: string } | null;
  copyRefusal?: { code: string; message: string } | null };
/** How the form names the source it records for, by kind. Each key written in full so a grep finds it; an unknown
 *  kind is never offered (the server sends only these three). */
const FOR_KEY: Record<string, string> = {
  tax_line: 'pa_manual_for_tax_line', bank_line: 'pa_manual_for_bank_line', cash_line: 'pa_manual_for_cash_line',
};
/** A refusal said in the reader's language where Reverse would be. Each key written in full so a grep finds it;
 *  'already_reversed' needs none (the row already says "Reversed JV-…"), and a code this file does not know is shown
 *  in the server's own words rather than as a button that can only be refused. */
const REV_REF_KEY: Record<string, string> = {
  asset: 'pa_manual_rev_ref_asset', asset_reversal: 'pa_manual_rev_ref_asset_reversal',
  reversal_of_released: 'pa_manual_rev_ref_reversal_of_released', no_open_period: 'pa_manual_rev_ref_no_open_period',
  no_lines: 'pa_manual_rev_ref_no_lines',
};
/** The statutory names stay Thai in every language (the R6 house rule), so they are written here, never in i18n. */
const REMIT_KIND_WORD: Record<string, string> = { pnd1: 'ภ.ง.ด.1', sso: 'สปส.' };
type Line = { accountId: string; dr: string; cr: string };

// [value, statutory journal name key, gloss key]. The journal name is what the book is called in the
// statute and stays Thai in every locale; only the gloss beside it is translated.
const BOOKS: [string, string, string][] = [
  ['general', 'pa_jnl_general', 'pa_manual_book_general_gloss'], ['cash', 'pa_jnl_cash', 'pa_manual_book_cash_gloss'],
  ['bank', 'pa_jnl_bank', 'pa_manual_book_bank_gloss'],
  ['purchase', 'pa_jnl_purchase', 'pa_manual_book_purchase_gloss'], ['sale', 'pa_jnl_sale', 'pa_manual_book_sale_gloss'],
];
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const satang = (v: string) => Math.round((parseFloat(String(v).replace(/,/g, '')) || 0) * 100);
const baht = (s: number) => money(Number(s || 0) / 100);

const btn: React.CSSProperties = {
  padding: '7px 12px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const EMPTY: Line[] = [{ accountId: '', dr: '', cr: '' }, { accountId: '', dr: '', cr: '' }];

export default function ManualEntry({ clientId, periodId, periodClosed, onChanged, openFor }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void;
    /** (W2 ship) Open the New form made FOR this source on arrival — an entries panel's "Record by hand". */
    openFor?: HandSource | null }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [open, setOpen] = useState<'' | 'new' | 'reverse' | 'import'>('');
  // The import's two states: the file the operator picked, and the server's verdict on it.
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [time, setTime] = useState<any>(null);

  const [date, setDate] = useState('');
  const [book, setBook] = useState('general');
  // A bank book is kept PER ACCOUNT NUMBER, so the account is part of the entry — createManualEntry
  // refuses a bank draft without one, and journalPost refuses it again at posting. The dropdown has
  // offered the bank book since it was written while the form never sent this field, so choosing
  // บัญชีธนาคาร always failed and no bank entry has ever been made (0 in production, 2026-10-02).
  const [bankAcct, setBankAcct] = useState('');
  const [bankAccts, setBankAccts] = useState<{ id: string; entries: number; onFile: boolean }[] | null>(null);
  const [bankTyping, setBankTyping] = useState(false);
  const [docNo, setDocNo] = useState('');
  const [narration, setNarration] = useState('');
  const [calcNote, setCalcNote] = useState('');
  const [reverseOn, setReverseOn] = useState('');
  const [lines, setLines] = useState<Line[]>(EMPTY);
  // The source this entry is recorded FOR, by hand (W1 ship, services/handBooks.ts): set only by Record by hand on a
  // refused row of the reversal picker. The entry then holds the source, so Generate never books it a second time;
  // the form says so, and offers no reversal date (the server refuses one on such an entry).
  const [forSource, setForSource] = useState<HandSource | null>(null);
  // (W3.1; W3.2) What the hand door will ask of an entry made for a statement line of a receipt (GET /journal/hand-door):
  // the WHT-prepaid accounts it may not debit — other entries carry the customer's withholding, or whose it would be cannot
  // be told — and why; or ('part') how much of the withholding is left for this line (`roomSat`: a customer who paid by
  // instalments has it booked one part per line). Save is held while the lines debit those accounts more than the door
  // takes, and the sentence says why: the door's 409 is never sent from here. `undefined` while it is being read (Save
  // waits for it), null when nothing is locked. (Batch 12 tail, the review) The rule and its sentence are ./handPrepaid's —
  // one copy, shared with the draft editor.
  const [handLock, setHandLock] = useState<HandDoorLock | null | undefined>(null);
  useEffect(() => {
    if (!forSource || forSource.kind !== 'bank_line') { setHandLock(null); return; }
    let live = true;
    setHandLock(undefined);
    api.jnlHandDoor(clientId, forSource.kind, forSource.id)
      .then((d: any) => { if (live) setHandLock(d?.prepaid && Array.isArray(d.prepaid.accountIds) ? d.prepaid : null); })
      .catch(() => { if (live) setHandLock(null); });   // the door still asks; a read that failed holds nothing back
    return () => { live = false; };
  }, [clientId, forSource]);

  const [q, setQ] = useState('');
  const [posted, setPosted] = useState<Posted[] | null>(null);
  // (Batch 11 W4 P9, LB-29) "Can be recorded by hand": only the rows whose way out is Record by hand — this period's and the
  // closed periods' — every one, 100 a page (GET /journal/posted?byHandOnly=1&page=), where the plain list stops at 100.
  const [byHandOnly, setByHandOnly] = useState(false);
  const [bhPage, setBhPage] = useState(1);
  const [bhTotal, setBhTotal] = useState(0);
  const readPosted = useCallback(() => api.jnlPosted(clientId, periodId, q, byHandOnly ? { page: bhPage } : undefined)
    .then((d: any) => { setPosted(d.entries || []); setBhTotal(Number(d.total) || 0); })
    .catch(() => { setPosted([]); setBhTotal(0); }), [clientId, periodId, q, byHandOnly, bhPage]);
  // The entry whose reason is being asked for (batch 11 R2): the route refuses a reversal without one.
  const [asking, setAsking] = useState<Posted | null>(null);

  useEffect(() => { api.jnlAccounts(clientId).then((d: any) => setAccounts(d.accounts || [])).catch(() => setAccounts([])); }, [clientId]);
  useEffect(() => {
    setBankAccts(null); setBankAcct(''); setBankTyping(false);
    api.jnlBankAccounts(clientId)
      .then((d: any) => {
        const list = d.accounts || [];
        setBankAccts(list);
        // One account is the overwhelmingly common case; preselecting it means the operator never
        // retypes the number and so can never start a second book by mistyping it.
        if (list.length === 1) setBankAcct(String(list[0].id));
        setBankTyping(list.length === 0);
      })
      .catch(() => { setBankAccts([]); setBankTyping(true); });
  }, [clientId]);

  const loadTime = useCallback(() => {
    api.jnlTimeliness(clientId, periodId).then((d: any) => setTime(d)).catch(() => setTime(null));
  }, [clientId, periodId]);
  useEffect(() => { loadTime(); }, [loadTime]);

  useEffect(() => {
    if (open !== 'reverse') return;
    const h = setTimeout(() => { readPosted(); }, q ? 220 : 0);
    return () => clearTimeout(h);
  }, [open, q, readPosted]);

  const dr = lines.reduce((s, l) => s + satang(l.dr), 0);
  const cr = lines.reduce((s, l) => s + satang(l.cr), 0);
  const balanced = dr === cr && dr > 0;
  // The server refuses it anyway (services/manualEntry.ts). Blocking here as well is so the
  // operator is told before they have typed the lines, not after.
  const bankReady = book !== 'bank' || !!bankAcct.trim();
  // (W3.1; W3.2) The WHT-prepaid debits of this entry the hand door would refuse (handLock above), by account: any, where
  // nothing of the withholding is left for this line ('taken', and whose-cannot-be-told); for 'part' and 'whole', more than
  // what is left (then 'over' — the door's own word for it). ./handPrepaid prepaidHoldOf.
  const prepaid = prepaidHoldOf(handLock, lines.map(l => ({ accountId: l.accountId, drSat: satang(l.dr) })));
  const handReady = !forSource || forSource.kind !== 'bank_line' || (handLock !== undefined && !prepaid.hit.length);

  const reset = () => { setDate(''); setDocNo(''); setNarration(''); setCalcNote(''); setReverseOn(''); setLines(EMPTY); setBook('general');
    setForSource(null);
    if ((bankAccts || []).length !== 1) setBankAcct(''); };

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlManual({
        clientId, periodId, entryDate: date, sourceBook: book, docNo, narration, calcNote,
        bankAccountId: book === 'bank' ? bankAcct.trim() : '',
        autoReverseOn: forSource ? '' : reverseOn,
        lines: lines.filter(l => l.accountId && (satang(l.dr) || satang(l.cr)))
          .map(l => ({ accountId: l.accountId, dr: satang(l.dr), cr: satang(l.cr) })),
        ...(forSource ? { forSource: { kind: forSource.kind, id: forSource.id } } : {}),
      });
      setMsg(t('pa_manual_saved', { docNo: r.docNo }));
      reset(); setOpen(''); onChanged(); loadTime();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // ⛔ Preview, then commit — the same file, the same checks, and the first call writes nothing.
  // An accountant does not commit a file blind, and the server refuses the whole file if any
  // voucher in it is refused, so what the preview says is what the commit does.
  const runImport = async (commit: boolean) => {
    if (!file) return;
    setBusy('import'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlImport(clientId, periodId, file, !commit);
      setPreview(r);
      if (commit) {
        setMsg(t('pa_manual_import_done', { n: r.created }));
        setFile(null); setPreview(null); setOpen('');
        onChanged?.();
      }
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // The same voucher on another date: the monthly bank charge, which is the thing that actually
  // recurs. The copy takes its own JV number — a document number is the identity of one piece of
  // paper, so it is never carried over.
  const repeat = async (p: Posted) => {
    const d = window.prompt(t('pa_manual_copy_prompt', { docNo: p.doc_no }), p.entry_date);
    if (!d) return;
    setBusy(p.id); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlCopy(p.id, d, periodId);
      setMsg(t('pa_manual_copy_done', { docNo: r.docNo }));
      onChanged?.();
    } catch (e: any) {
      // The copy door's refusal of an entry made by hand for a source (W1 ship), in the reader's language.
      setErr(e?.payload?.code === 'hand_copy' ? t('pa_manual_copy_ref_hand', { docNo: p.doc_no })
        : e?.payload?.code === 'settlement_copy' ? t('pa_manual_copy_ref_settlement', { docNo: p.doc_no }) : e.message);
    } finally { setBusy(''); }
  };

  // Reverse asks WHY first (batch 11 R2): the reason dialog took the place of the confirm, with the same sentence
  // as its line. A reversal raised closes it. ⛔ So does a REFUSAL (HTTP 4xx — reversed in another tab meanwhile, an
  // asset rule, a released source, no open period; W1 review): the books decided it, so a second press could only be
  // refused again and page the admins a second time. The server's words go on this card and the picker is read
  // again, so the row says why (or "Reversed by …") in place of Reverse; until that list is in, the loader stands
  // where the rows were, so nothing in the gap can be pressed. Only a network failure or a 5xx keeps the dialog open.
  const reverse = (p: Posted) => { setErr(''); setMsg(''); setAsking(p); };
  const rereadPosted = () => {
    setPosted(null);
    readPosted();
  };
  const raise = async (p: Posted, reason: string) => {
    setBusy(p.id);
    try {
      const r: any = await api.jnlReverse(p.id, reason);
      setAsking(null);
      setMsg(t('pa_manual_reversed_msg', { docNo: r.docNo, date: dmy(r.date) }));
      setQ(q => q); onChanged();
      rereadPosted();
    } finally { setBusy(''); }
  };
  const refused = (message: string) => { setAsking(null); setMsg(''); setErr(message); rereadPosted(); };
  // Record by hand (W1 ship): the New form, empty, made for the row's source.
  const byHand = (src: HandSource) => { if (periodClosed) return; reset(); setForSource(src); setErr(''); setMsg(''); setOpen('new'); };
  // (W2 ship) Arrived from an entries panel's "Record by hand": that form, once (never on a read-only seat or a closed period).
  useEffect(() => {
    if (openFor && openFor.kind && openFor.id && !readOnly && !periodClosed) byHand(openFor);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // What a payroll row says in place of Reverse: the run (R7's words), the payment and its run, or the remittance —
  // which has no run, so it names its month beside the statutory name.
  const payrollOwned = (r: PayrollRef): React.ReactNode => {
    if (r.kind === 'payment') return t('pa_manual_payroll_pay_owned', { payNo: r.payNo || '', docNo: r.docNo });
    if (r.kind === 'remittance') {
      const mmYyyy = r.year && r.month ? `${String(r.month).padStart(2, '0')}/${r.year}` : '';
      return <><span style={{ fontWeight: 700, marginRight: 4 }}>{REMIT_KIND_WORD[String(r.remitKind || '')] || ''}</span>{t('pa_manual_payroll_remit_owned', { month: mmYyyy })}</>;
    }
    return t('pa_manual_payroll_owned', { docNo: r.docNo });
  };

  const sweep = async () => {
    setBusy('sweep'); setErr(''); setMsg('');
    try {
      const r: any = await api.jnlDueReversals(clientId, periodId);
      setMsg(r.created ? t('pa_manual_sweep_done', { n: r.created }) : t('pa_manual_sweep_none'));
      onChanged();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-pencil-plus" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_manual_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {t('pa_manual_subtitle')}
          </span>
        </span>
        {!periodClosed && !readOnly && (<>
          <button onClick={() => { setOpen(o => o === 'new' ? '' : 'new'); setForSource(null); setErr(''); }} style={{ ...btn, background: open === 'new' ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-plus" aria-hidden /> {t('pa_manual_new_btn')}
          </button>
          <button onClick={() => { setOpen(o => o === 'reverse' ? '' : 'reverse'); setErr(''); }} style={{ ...btn, background: open === 'reverse' ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-arrow-back-up" aria-hidden /> {t('pa_manual_reverse_btn')}
          </button>
          <button onClick={() => { setOpen(o => o === 'import' ? '' : 'import'); setErr(''); setMsg(''); setFile(null); setPreview(null); }}
            style={{ ...btn, background: open === 'import' ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
            <i className="ti ti-file-upload" aria-hidden /> {t('pa_manual_import_btn')}
          </button>
          <button onClick={sweep} disabled={!!busy} title={t('pa_manual_sweep_title')} style={btn}>
            <i className={busy === 'sweep' ? 'ti ti-loader' : 'ti ti-refresh'} aria-hidden /> {t('pa_manual_accruals_btn')}
          </button>
        </>)}
      </div>

      {/* The DBD's entry deadlines, stated where the entries are made rather than in a report nobody opens. */}
      {time && (!!time.lateEntries?.length || time.closingOverdue) && (
        <div style={{ fontSize: 11.5, color: '#92400E', background: '#FFFBEB', borderTop: '1px solid #F1C232', padding: '8px 13px', lineHeight: 1.7 }}>
          {!!time.lateEntries?.length && (
            <div><i className="ti ti-clock-exclamation" aria-hidden /> {t('pa_manual_late_entries', { n: time.lateEntries.length, days: time.lateEntries[0].days, docNo: time.lateEntries[0].docNo })}
              {' '}{t('pa_manual_late_rule')}</div>
          )}
          {time.closingOverdue && (
            <div><i className="ti ti-alert-triangle" aria-hidden /> {t('pa_manual_closing_overdue', { date: dmy(time.closingOverdue.endDate), days: time.closingOverdue.daysSince })}</div>
          )}
        </div>
      )}

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {open === 'new' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          {forSource && FOR_KEY[forSource.kind] && (
            <div data-for-source={forSource.kind} style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-app)', border: '1px solid var(--border)',
              borderRadius: 7, padding: '7px 10px', marginBottom: 9, lineHeight: 1.55 }}>
              <i className="ti ti-link" aria-hidden /> {t('pa_manual_for_banner', { what: t(FOR_KEY[forSource.kind], { label: forSource.label }) })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_date')}<br />
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_book')}<br />
              <select value={book} onChange={e => setBook(e.target.value)} style={{ ...inp, width: 170 }}>
                {BOOKS.map(([k, nameKey, glossKey]) => <option key={k} value={k}>{`${t(nameKey)} · ${t(glossKey)}`}</option>)}
              </select></label>
            {book === 'bank' && (
              <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                {t('pa_manual_f_bank_acct')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_bank_acct_hint')}</span><br />
                {bankTyping || !(bankAccts || []).length ? (
                  <>
                    <input value={bankAcct} onChange={e => setBankAcct(e.target.value)}
                      placeholder={t('pa_manual_f_bank_acct_ph')} style={{ ...inp, width: 190 }} />
                    {!(bankAccts || []).length && (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 330, marginTop: 3, lineHeight: 1.45 }}>
                        {t('pa_manual_bank_acct_none')}
                      </div>
                    )}
                  </>
                ) : (
                  <select value={bankAcct}
                    onChange={e => { if (e.target.value === '\u0000new') { setBankTyping(true); setBankAcct(''); } else setBankAcct(e.target.value); }}
                    style={{ ...inp, width: 190 }}>
                    <option value=""></option>
                    {(bankAccts || []).map(a => (
                      <option key={a.id} value={a.id}>{a.entries ? `${a.id} · ${t('pa_manual_bank_acct_used', { n: a.entries })}` : a.id}</option>
                    ))}
                    <option value={'\u0000new'}>{t('pa_manual_bank_acct_new')}</option>
                  </select>
                )}
              </label>
            )}
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_docno')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_docno_hint')}</span><br />
              <input value={docNo} onChange={e => setDocNo(e.target.value)} placeholder={t('pa_manual_f_docno_ph')} style={{ ...inp, width: 190 }} /></label>
            {!forSource && (
              <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_manual_f_reverse_on')} <span style={{ color: 'var(--text-tertiary)' }}>{t('pa_manual_f_reverse_on_hint')}</span><br />
                <input type="date" value={reverseOn} onChange={e => setReverseOn(e.target.value)} style={{ ...inp, width: 150 }} /></label>
            )}
          </div>
          <input value={narration} onChange={e => setNarration(e.target.value)} placeholder={t('pa_manual_narration_ph')}
            style={{ ...inp, width: '100%', marginBottom: 7 }} />
          <input value={calcNote} onChange={e => setCalcNote(e.target.value)}
            placeholder={t('pa_manual_calcnote_ph')}
            style={{ ...inp, width: '100%', marginBottom: 9 }} />

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-tertiary)', padding: '0 0 4px' }}>{t('pa_manual_th_account')}</th>
              <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', width: 130 }}>{t('pa_manual_th_dr')}</th>
              <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', width: 130 }}>{t('pa_manual_th_cr')}</th>
              <th style={{ width: 30 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 0' }}>
                    <select value={l.accountId} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, accountId: e.target.value } : x))}
                      style={{ ...inp, width: '100%', maxWidth: 420 }}>
                      <option value="">{t('pa_manual_pick_account')}</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th} — {a.name_en}</option>)}
                    </select>
                  </td>
                  {/* One side per line: typing in one clears the other, which is the CHECK constraint
                      the ledger enforces anyway — better met here than as an error after saving. */}
                  <td style={{ padding: '3px 0 3px 6px' }}>
                    <input value={l.dr} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, dr: e.target.value, cr: '' } : x))}
                      inputMode="decimal" style={{ ...inp, width: '100%', textAlign: 'right' }} /></td>
                  <td style={{ padding: '3px 0 3px 6px' }}>
                    <input value={l.cr} onChange={e => setLines(ls => ls.map((x, j) => j === i ? { ...x, cr: e.target.value, dr: '' } : x))}
                      inputMode="decimal" style={{ ...inp, width: '100%', textAlign: 'right' }} /></td>
                  <td style={{ textAlign: 'right' }}>
                    {lines.length > 2 && <button onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}
                      style={{ ...btn, padding: '2px 6px', fontSize: 11, color: '#B42318' }}><i className="ti ti-x" aria-hidden /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td style={{ paddingTop: 7 }}>
                <button onClick={() => setLines(ls => [...ls, { accountId: '', dr: '', cr: '' }])} style={{ ...btn, padding: '4px 9px', fontSize: 11.5 }}>
                  <i className="ti ti-plus" aria-hidden /> {t('pa_manual_add_line')}</button>
              </td>
              <td style={{ textAlign: 'right', paddingTop: 7, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{baht(dr)}</td>
              <td style={{ textAlign: 'right', paddingTop: 7, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{baht(cr)}</td>
              <td />
            </tr></tfoot>
          </table>

          {/* (W3.1; W3.2) The customer's withholding is booked already — all of it, or ('over') more than this entry's share
              would leave room for — or whose it would be cannot be told: the hand door would refuse this entry as it
              debits the WHT-prepaid account — said here, with Save held, the server's words as hover. */}
          {!!prepaid.hit.length && handLock && (
            <div data-manual-prepaid={prepaid.caseKey} title={handLock.message}
              style={{ fontSize: 12, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9', borderRadius: 7, padding: '7px 10px', marginTop: 9, lineHeight: 1.55 }}>
              <i className="ti ti-alert-triangle" aria-hidden /> <PrepaidSentence lock={handLock} hold={prepaid} codeOf={id => accounts.find(a => a.id === id)?.code || ''} />
            </div>
          )}
          {/* (Batch 11 W4 P9, W3.2 residual) A receipt's instalment line recorded by hand with NOTHING on the WHT-prepaid account
              while some of the customer's withholding is still left for it: one line saying how much it may carry — a hint, never
              a refusal (the person decides; this line may truly carry none). */}
          {handLock && handLock.case === 'part' && !prepaid.debits && Number(handLock.roomSat || 0) > 0 && (
            <div data-manual-prepaid-room={Number(handLock.roomSat || 0)} style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 8 }}>
              <i className="ti ti-info-circle" aria-hidden /> {t('pa_manual_prepaid_room_hint', { receipt: handLock.params?.receipt || '—', room: baht(Number(handLock.roomSat || 0)) })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: balanced ? '#166534' : '#B45309' }}>
              {dr === cr && dr === 0 ? t('pa_manual_no_amounts') : balanced ? t('pa_manual_balances') : t('pa_manual_out_by', { amount: baht(Math.abs(dr - cr)) })}
            </span>
            <div style={{ flex: 1 }} />
            <button onClick={() => { reset(); setOpen(''); }} style={btn}>{t('cancel')}</button>
            <button onClick={save} disabled={!balanced || !bankReady || !handReady || !!busy} data-manual-save
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (!balanced || !bankReady || !handReady || busy) ? 0.5 : 1 }}>
              <i className={busy === 'save' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_manual_save_draft')}
            </button>
          </div>
        </div>
      )}

      {open === 'import' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {t('pa_manual_import_note')}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="file" accept=".csv,text/csv" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setErr(''); }} style={{ fontSize: 12 }} />
            <button onClick={() => runImport(false)} disabled={!file || !!busy} style={{ ...btn, opacity: (!file || busy) ? 0.5 : 1 }}>
              <i className={busy === 'import' ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('pa_manual_import_preview')}
            </button>
            <button onClick={() => runImport(true)} disabled={!file || !!busy || !preview || preview.refused > 0}
              title={preview?.refused ? t('pa_manual_import_blocked') : undefined}
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (!file || busy || !preview || preview.refused > 0) ? 0.5 : 1 }}>
              <i className="ti ti-device-floppy" aria-hidden /> {t('pa_manual_import_commit')}
            </button>
          </div>
          {preview && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: preview.refused ? '#B42318' : '#166534', marginBottom: 6 }}>
                {t('pa_manual_import_summary', { ok: preview.ok, refused: preview.refused })}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {(preview.groups || []).map((g: any, i: number) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)', background: g.error ? '#FEF2F2' : undefined }}>
                      <td style={{ padding: '6px 6px 6px 0', whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{dmy(g.date)}</td>
                      <td style={{ padding: '6px', fontWeight: 700, whiteSpace: 'nowrap' }}>{g.docNo || '—'}</td>
                      <td style={{ padding: '6px', minWidth: 0 }}>{g.narration}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{baht(g.total / 100)}</td>
                      <td style={{ padding: '6px', fontSize: 11.5, color: g.error ? '#B42318' : '#166534' }}>
                        {g.error || t('pa_manual_import_will_enter')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {open === 'reverse' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {t('pa_manual_reverse_note')}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 9, flexWrap: 'wrap' }}>
            <input value={q} onChange={e => { setQ(e.target.value); setBhPage(1); }} placeholder={t('pa_manual_search_ph')}
              style={{ ...inp, flex: '1 1 240px', minWidth: 0 }} />
            <button type="button" data-by-hand-filter={byHandOnly ? 'on' : 'off'} aria-pressed={byHandOnly}
              onClick={() => { setPosted(null); setBhPage(1); setByHandOnly(v => !v); }}
              style={{ border: `1px solid ${byHandOnly ? 'var(--accent)' : 'var(--border-strong)'}`, borderRadius: 999, padding: '4px 11px', fontSize: 11.5, fontWeight: 700,
                background: byHandOnly ? 'var(--accent)' : 'var(--bg-surface)', color: byHandOnly ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <i className="ti ti-hand-finger" aria-hidden /> {t('pa_manual_filter_by_hand')}
            </button>
          </div>
          {posted === null ? <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
            : !posted.length ? <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5 }}>{t('pa_manual_none_posted')}</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {posted.map(p => (
                    <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 6px 6px 0', whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{dmy(p.entry_date)}</td>
                      <td style={{ padding: '6px', fontWeight: 700 }}>{p.doc_no}</td>
                      <td style={{ padding: '6px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.closedPeriod && (
                          <span data-closed-period="1" style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', border: '1px solid var(--border)', borderRadius: 999, padding: '0 6px', marginRight: 6 }}>
                            {t('pa_manual_row_closed_period', { from: dmy(p.closedPeriod.from), to: dmy(p.closedPeriod.to) })}
                          </span>
                        )}
                        {p.narration}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{baht(p.amount)}</td>
                      <td style={{ padding: '6px 0 6px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {/* ⛔ No Repeat on an entry made by hand for a source, nor on its reversal (W1 ship): the copy door
                            refuses it (409 hand_copy) — a copy would book the figures again holding nothing. Why, as a hover. */}
                        {!p.closedPeriod && p.copyRefusal && (
                          <span data-copy-refused={p.copyRefusal.code} title={t(p.copyRefusal.code === 'settlement_copy' ? 'pa_manual_copy_ref_settlement' : 'pa_manual_copy_ref_hand', { docNo: p.doc_no })}
                            aria-label={t(p.copyRefusal.code === 'settlement_copy' ? 'pa_manual_copy_ref_settlement' : 'pa_manual_copy_ref_hand', { docNo: p.doc_no })}
                            style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 6 }}>
                            <i className="ti ti-lock" aria-hidden />
                          </span>
                        )}
                        {!p.closedPeriod && !p.copyRefusal && (
                          <button onClick={() => repeat(p)} disabled={!!busy || periodClosed} title={t('pa_manual_copy_title')}
                            style={{ ...btn, padding: '3px 9px', fontSize: 11.5, marginRight: 6 }}>
                            <i className={busy === p.id ? 'ti ti-loader' : 'ti ti-copy'} aria-hidden /> {t('pa_manual_copy_btn')}
                          </button>
                        )}
                        {/* ⛔ A payroll voucher — the accrual or its reversal (batch 10 R7); a payment of the net pay or
                            a PND.1 / SSO remittance, and their reversals (R8) — is never reversed here: the ledger door
                            refuses it to anything but payroll, so a Reverse on it could only ever be a 409 that pages
                            the admins. It says what owns it instead. Copy stays (R7-18): it makes an ordinary JV,
                            which the run's next Post lists as a possible double entry. */}
                        {p.reversed_by && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('pa_manual_reversed_by', { docNo: p.reversed_by })}</span>}
                        {/* ⛔ …nor an entry the server would refuse to reverse for any other reason (batch 11 R2): an
                            asset entry the asset rules hold, a reversal whose original's source was released
                            (reversal_of_released), no open period. The row says why in place of Reverse; the
                            server's sentence is the hover text. */}
                        {p.payroll
                          ? <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: p.reversed_by ? 6 : 0 }} data-payroll-owned={p.payroll.kind || 'run'}>{payrollOwned(p.payroll)}</span>
                          : p.reversed_by ? null
                          : p.reverseRefusal
                            ? <>
                                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'normal' }} title={p.reverseRefusal.message} data-rev-refused={p.reverseRefusal.code}>
                                  <i className="ti ti-lock" aria-hidden /> {REV_REF_KEY[p.reverseRefusal.code] ? t(REV_REF_KEY[p.reverseRefusal.code], { docNo: p.doc_no }) : p.reverseRefusal.message}
                                </span>
                                {/* Where the way out is a manual entry made for the source (W1 ship): the form for it, here,
                                    so the correction holds the source and Generate never books it a second time. ⛔ Never
                                    for a closed period (as Copy): the form would save into it, refused — the server offers
                                    the door only on the open period's list, and this card is keyed by period. */}
                                {p.reverseRefusal.byHand && FOR_KEY[p.reverseRefusal.byHand.kind] && (
                                  <button onClick={() => byHand(p.reverseRefusal!.byHand!)} disabled={!!busy || periodClosed} title={t('pa_manual_by_hand_title')}
                                    data-by-hand={p.reverseRefusal.byHand.kind} style={{ ...btn, padding: '3px 9px', fontSize: 11.5, marginLeft: 6 }}>
                                    <i className="ti ti-pencil" aria-hidden /> {t('pa_manual_by_hand_btn')}
                                  </button>
                                )}
                              </>
                            : <button onClick={() => reverse(p)} disabled={!!busy} style={{ ...btn, padding: '3px 9px', fontSize: 11.5 }}>{t('pa_manual_reverse_btn')}</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          {byHandOnly && bhTotal > 100 && (
            <div data-by-hand-pages={bhTotal} style={{ marginTop: 8 }}>
              <Pagination page={bhPage} total={bhTotal} limit={100} onPage={n => { setPosted(null); setBhPage(n); }} />
            </div>
          )}
        </div>
      )}

      {asking && (
        <ReverseReasonDialog docNo={asking.doc_no} line={t('pa_manual_reverse_confirm', { docNo: asking.doc_no })}
          onClose={() => setAsking(null)} onSubmit={reason => raise(asking, reason)} onRefused={refused} />
      )}
    </div>
  );
}
